import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Users, Mail, Phone, MapPin, Trash2, ChevronRight, Hammer, AlertCircle, Plus, X } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, cn } from '../utils/formatters';
import { modalOverlay, modalContent, btnGhost } from '../styles/components';

const statusLabels: Record<string, string> = {
  planned: 'Planificado',
  in_progress: 'En curso',
  paused: 'Pausado',
  finished: 'Finalizado',
  cancelled: 'Cancelado',
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { clients, projects, budgets, deleteClient } = useCompany();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const client = clients.find(c => c.id === id);
  const clientProjects = projects.filter(p => p.clientId === id);
  const clientBudgets = budgets.filter(b => b.clientId === id);

  if (!client) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-danger-500" />
          <h1 className="text-2xl font-bold text-danger-600">Cliente no encontrado</h1>
          <p className="b2b-muted mb-4">El cliente con ID {id} no existe.</p>
          <button onClick={() => navigate('/clients')} className="b2b-btn b2b-btn--primary">
            <Plus className="w-4 h-4" /> Ver clientes
          </button>
        </div>
      </div>
    );
  }

  const fields = [
    { label: 'Nombre', value: client.name },
    { label: 'Razón social', value: client.fiscalName || '—' },
    { label: 'CIF / NIF', value: client.cif || '—' },
    { label: 'Email', value: client.email || '—', breakAll: true },
    { label: 'Teléfono', value: client.phone || '—' },
    { label: 'Dirección', value: client.address || '—' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Cliente</span>
          <h1 className="text-3xl font-extrabold tracking-tight">{client.name}</h1>
          <p className="b2b-muted">Información completa del cliente</p>
        </div>
        <button onClick={() => navigate('/clients')} className="b2b-btn b2b-btn--ghost">
          <X className="w-4 h-4" /> Volver
        </button>
      </div>

      <div className="b2b-panel">
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {fields.map(field => (
            <div key={field.label}>
              <p className="b2b-card__label">{field.label}</p>
              <p className={cn('text-lg font-semibold', field.breakAll && 'break-all')}>{field.value}</p>
            </div>
          ))}
          {client.notes && (
            <div className="sm:col-span-2">
              <p className="b2b-card__label">Notas</p>
              <p className="b2b-muted">{client.notes}</p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 px-6 pb-6 pt-6 border-t border-[var(--b2b-border)]">
          <a href={`mailto:${client.email || ''}`} className="b2b-btn b2b-btn--ghost b2b-btn--sm">
            <Mail className="w-4 h-4" /> Email
          </a>
          <a href={`tel:${client.phone || ''}`} className="b2b-btn b2b-btn--ghost b2b-btn--sm">
            <Phone className="w-4 h-4" /> Llamar
          </a>
          {client.address && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(client.address)}`} target="_blank" rel="noreferrer" className="b2b-btn b2b-btn--ghost b2b-btn--sm">
              <MapPin className="w-4 h-4" /> Mapa
            </a>
          )}
        </div>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Proyectos asociados</h2>
          <span className="text-sm b2b-muted">{clientProjects.length}</span>
        </div>
        {clientProjects.length === 0 ? (
          <div className="p-8 text-center b2b-muted">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No hay proyectos asociados a este cliente</p>
          </div>
        ) : (
          clientProjects.map(project => (
            <Link key={project.id} to={`/projects/${project.id}`} className="b2b-panel__row">
              <span className="b2b-icon"><Hammer className="w-5 h-5" /></span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium truncate">{project.name}</span>
                <span className="block text-xs b2b-muted">{statusLabels[project.status] || project.status}</span>
              </span>
              <ChevronRight className="w-4 h-4 b2b-muted" />
            </Link>
          ))
        )}
      </div>

      {clientBudgets.length > 0 && (
        <div className="b2b-panel">
          <div className="b2b-panel__head">
            <h2 className="text-lg font-semibold">Presupuestos</h2>
            <span className="text-sm b2b-muted">{clientBudgets.length}</span>
          </div>
          {clientBudgets.map(budget => (
            <Link key={budget.id} to={`/budgets/${budget.id}`} className="b2b-panel__row">
              <span className="flex-1 min-w-0">
                <span className="block font-medium">{budget.number}</span>
                <span className="block text-xs b2b-muted">{formatCurrency(budget.total)}</span>
              </span>
              <span className={cn('b2b-pill', budget.status === 'accepted' ? 'b2b-pill--ok' : budget.status === 'sent' ? 'b2b-pill--warn' : '')}>
                {({ draft: 'Borrador', sent: 'Enviado', accepted: 'Aceptado', rejected: 'Rechazado', expired: 'Caducado' } as Record<string, string>)[budget.status] || budget.status}
              </span>
              <ChevronRight className="w-4 h-4 b2b-muted" />
            </Link>
          ))}
        </div>
      )}

      <div className="pt-6 border-t border-[var(--b2b-border)]">
        <button onClick={() => setShowDeleteModal(true)} className="b2b-btn b2b-btn--danger" aria-label="Eliminar cliente">
          <Trash2 className="w-4 h-4" /> Eliminar cliente
        </button>

        {showDeleteModal && (
          <div className={modalOverlay}>
            <div className={cn(modalContent, 'p-8 max-w-md w-full')} role="dialog" aria-modal="true">
              <h2 className="text-xl font-bold text-center mb-2">Cuidado</h2>
              <p className="b2b-muted mb-8 text-center">¿Estás seguro de querer eliminar este cliente? Esta acción no se puede deshacer.</p>
              <div className="flex gap-3 justify-end pt-6 border-t border-[var(--b2b-border)]">
                <button onClick={() => setShowDeleteModal(false)} className={btnGhost}>Cancelar</button>
                <button
                  onClick={() => {
                    deleteClient(client.id);
                    setShowDeleteModal(false);
                    navigate('/clients');
                  }}
                  className="b2b-btn b2b-btn--danger"
                >
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
